// Plazos del aviso al IESS (lote E). Fechas como texto AAAA-MM-DD, sin zona horaria.
// Norma (verificada el 31/10/2026): Resolución C.D. 513 del IESS (Reglamento del Seguro General de Riesgos del Trabajo)
// y la página oficial del IESS: el empleador presenta el aviso en línea en el término de 10 días, contados desde la
// fecha del siniestro (accidente) o desde que se advierten los indicios (enfermedad profesional u ocupacional).
// "Término" = días hábiles: se cuentan de lunes a viernes. El cálculo NO descuenta feriados (no hay una lista oficial
// cargada): la pantalla lo dice y recomienda enviar el aviso antes.

import { diaEc } from '../fechas';

export const DIAS_HABILES_AVISO = 10;
export const NOTA_TERMINO = 'Se cuentan 10 días hábiles (de lunes a viernes) desde el día siguiente al siniestro. '
  + 'No descuenta feriados: conviene enviar el aviso antes de esa fecha.';
export const NOTA_TERMINO_ENFERMEDAD = 'Se cuentan 10 días hábiles (de lunes a viernes) desde el día siguiente a aquel en que se advirtieron los indicios. '
  + 'No descuenta feriados: conviene enviar el aviso antes de esa fecha.';

const RE_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;

function aUtc(fecha: string): Date {
  const m = RE_FECHA.exec(fecha);
  if (!m) throw new Error(`Fecha no válida: ${fecha}`);
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
}
const aIso = (d: Date) => d.toISOString().slice(0, 10);

export const esFechaIso = (f: string | null | undefined): f is string => !!f && RE_FECHA.test(f) && !Number.isNaN(aUtc(f).getTime());

/** Hoy en la hora local del equipo, como AAAA-MM-DD. */
export function hoyIso(hoy: Date = new Date()): string {
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
}

export const esDiaHabil = (fecha: string) => { const d = aUtc(fecha).getUTCDay(); return d !== 0 && d !== 6; };

/** Suma días hábiles (lunes a viernes) a partir del día siguiente a la fecha dada. */
export function sumarDiasHabiles(fecha: string, dias: number): string {
  const d = aUtc(fecha);
  let cuenta = 0;
  while (cuenta < dias) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dia = d.getUTCDay();
    if (dia !== 0 && dia !== 6) cuenta++;
  }
  return aIso(d);
}

/** Último día del término para presentar el aviso (siniestro o indicios + 10 días hábiles). */
export const fechaLimiteAviso = (fechaCaso: string) => sumarDiasHabiles(fechaCaso, DIAS_HABILES_AVISO);

/** La fecha más N días de calendario. */
export function sumarDias(fecha: string, dias: number): string {
  const d = aUtc(fecha);
  d.setUTCDate(d.getUTCDate() + dias);
  return aIso(d);
}

/** Días de calendario de "desde" a "hasta" (negativo si "hasta" es anterior). */
export function diasEntre(desde: string, hasta: string): number {
  return Math.round((aUtc(hasta).getTime() - aUtc(desde).getTime()) / 86400000);
}

/** La fecha menos N meses (el día se ajusta al último del mes si no existe). */
export function restarMeses(fecha: string, meses: number): string {
  const d = aUtc(fecha);
  const y = d.getUTCFullYear(), m = d.getUTCMonth() - meses, dia = d.getUTCDate();
  const ultimo = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return aIso(new Date(Date.UTC(y, m, Math.min(dia, ultimo))));
}

/** dd/mm/aaaa para pantalla y PDF. */
export function fechaCorta(fecha: string | null | undefined): string {
  if (!fecha) return '—';
  const m = RE_FECHA.exec(diaEc(fecha));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : fecha;
}

/** "Vence hoy", "Vence mañana", "Vence en N días" o "Vencido hace N días". */
export function textoVence(fechaLimite: string, hoy: string): string {
  const n = diasEntre(hoy, fechaLimite);
  if (n === 0) return 'Vence hoy';
  if (n === 1) return 'Vence mañana';
  if (n > 1) return `Vence en ${n} días`;
  return n === -1 ? 'Vencido hace 1 día' : `Vencido hace ${-n} días`;
}
